import { formatGr, normKey, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { BottomSheet } from '../components/BottomSheet';
import { PriceChart } from '../components/prices/PriceChart';
import { cardCls, inputCls, sectionTitleCls } from '../components/ui/classes';
import { PageHeader } from '../components/ui/PageHeader';
import { SkeletonRows } from '../components/ui/Skeleton';
import { setArticleProduct } from '../data/articles';
import { priceStats, useArticleHistory, useProductHistory, type History, type Purchase } from '../data/history';
import { agoLabel, articleName } from '../data/prices';
import { PROMO_SOURCE, promoShort, usePromoIndex } from '../data/promotions';
import { db } from '../db/db';
import type { ArticleRow } from '../db/types';

const PURCHASES_SHOWN = 30;

const dayLabel = (ms: number) => new Date(ms).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });

/** Historia ceny produktu Odhaczki: wszystkie jego artykuły z paragonów. */
export function ProductPage() {
  const { id } = useParams();
  const history = useProductHistory(id);
  const [filter, setFilter] = useState<Id | null>(null);
  if (history === undefined) return <Loading />;
  if (!history?.product) return <Missing />;
  const bought = history.articles.filter((a) => history.purchases.some((p) => p.articleId === a.id));
  const purchases = filter ? history.purchases.filter((p) => p.articleId === filter) : history.purchases;
  return (
    <>
      <PageHeader title={history.product.name} back="/prices" />
      <main className="flex flex-1 flex-col gap-5 p-4 pb-8">
        {bought.length > 1 && (
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            <Chip active={filter === null} onClick={() => setFilter(null)}>
              Wszystkie
            </Chip>
            {bought.map((a) => (
              <Chip key={a.id} active={filter === a.id} onClick={() => setFilter(a.id)}>
                {articleName(a)}
              </Chip>
            ))}
          </div>
        )}
        <HistoryBody history={{ ...history, purchases }} showArticle={filter === null && bought.length > 1} />
        {history.articles.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className={sectionTitleCls}>Towary z paragonów ({history.articles.length})</h2>
            <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
              {history.articles.map((a) => (
                <li key={a.id}>
                  <ArticleLink article={a} />
                </li>
              ))}
            </ul>
          </section>
        )}
        {history.purchases.length === 0 && (
          <p className="text-sm text-stone-500">
            Ten produkt nie ma jeszcze ceny. Pojawi się, gdy paragon z Lidla zostanie dopasowany do pozycji listy albo gdy przypniesz konkretny towar.
          </p>
        )}
      </main>
    </>
  );
}

/** Historia ceny jednego artykułu (konkretnego towaru z paragonów) i jego produkt w Odhaczce. */
export function ArticlePage() {
  const { id } = useParams();
  const history = useArticleHistory(id);
  const [picker, setPicker] = useState(false);
  if (history === undefined) return <Loading />;
  if (!history) return <Missing />;
  const article = history.articles[0]!;
  return (
    <>
      <PageHeader title={articleName(article)} back="/prices" />
      <main className="flex flex-1 flex-col gap-5 p-4 pb-8">
        {article.rawName && article.rawName !== articleName(article) && <p className="-mt-3 text-sm text-stone-500">Na paragonie: {article.rawName}</p>}
        <HistoryBody history={history} showArticle={false} />
        <section className="flex flex-col gap-2">
          <h2 className={sectionTitleCls}>Produkt w Odhaczce</h2>
          <div className={`${cardCls} flex items-center gap-3 p-4`}>
            <div className="min-w-0 flex-1">
              {history.product ? (
                <Link to={`/products/${encodeURIComponent(history.product.id)}`} className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-500">
                  {history.product.name}
                </Link>
              ) : (
                <span className="text-stone-500">niepodpięty — jego cena nie pokaże się przy pozycjach list</span>
              )}
            </div>
            <button type="button" onClick={() => setPicker(true)} className="min-h-11 shrink-0 px-2 text-sm font-medium text-brand-700 dark:text-brand-500">
              {history.product ? 'zmień' : 'podepnij'}
            </button>
          </div>
        </section>
      </main>
      <ProductPicker
        open={picker}
        initial={articleName(article)}
        hasProduct={!!history.product}
        onClose={() => setPicker(false)}
        onPick={(productId) => {
          setPicker(false);
          void setArticleProduct(article.id, productId);
        }}
      />
    </>
  );
}

function HistoryBody({ history, showArticle }: { history: History; showArticle: boolean }) {
  const [all, setAll] = useState(false);
  const promos = usePromoIndex();
  const now = history.articles.flatMap((a) => promos?.byArticle.get(a.id) ?? []);
  const stats = priceStats(history.purchases);
  const last = stats.last;
  const perKg = history.purchases.length > 0 && history.purchases.every((p) => p.weight);
  const unit = perKg ? '/kg' : '';
  const names = new Map(history.articles.map((a) => [a.id, articleName(a)]));
  if (!last) return null;
  const shown = all ? history.purchases : history.purchases.slice(0, PURCHASES_SHOWN);
  return (
    <>
      {now.length > 0 && (
        <section className="flex flex-col gap-1 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-900/50 dark:bg-amber-900/20">
          <p className="font-semibold text-amber-900 dark:text-amber-100">Teraz w promocji</p>
          {now.map((p) => (
            <p key={p.id}>
              {p.title}: <b>{promoShort(p)}</b>
              {p.priceGr ? ` · ${formatGr(p.priceGr)}` : ''}
              <span className="text-stone-500"> ({PROMO_SOURCE[p.source ?? ''] ?? 'promocja'})</span>
            </p>
          ))}
        </section>
      )}
      <section className={`${cardCls} flex flex-col gap-3 p-4`}>
        <div>
          <p className="text-sm text-stone-500">Ostatnio · {agoLabel(last.ms)}</p>
          <p className="text-3xl font-bold tabular-nums">
            {formatGr(last.shelfGr)}
            <span className="text-lg font-semibold">{unit}</span>
          </p>
          {last.paidGr < last.shelfGr && (
            <p className="text-sm text-brand-700 tabular-nums dark:text-brand-500">
              zapłacone {formatGr(last.paidGr)}
              {unit}
              {last.discounts ? ` (${last.discounts})` : ''}
            </p>
          )}
        </div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          <Stat label="Najniżej w roku" value={stats.minShelfGr !== null ? `${formatGr(stats.minShelfGr)}${unit}` : '—'} />
          <Stat label="Zwykle" value={stats.typicalShelfGr !== null ? `${formatGr(stats.typicalShelfGr)}${unit}` : '—'} />
          <Stat
            label="Ostatnia promocja"
            value={stats.lastPromo ? `${formatGr(stats.lastPromo.paidGr)}${unit} · ${dayLabel(stats.lastPromo.ms)}` : 'brak'}
          />
          <Stat label="W ostatnim roku" value={`${stats.count12}× · ${formatGr(stats.spent12Gr)}`} />
        </dl>
        {history.purchases.length > 1 && <PriceChart purchases={history.purchases} perKg={perKg} />}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className={sectionTitleCls}>Zakupy ({history.purchases.length})</h2>
        <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
          {shown.map((p) => (
            <PurchaseRow key={p.lineId} p={p} name={showArticle ? names.get(p.articleId) : undefined} />
          ))}
        </ul>
        {!all && history.purchases.length > PURCHASES_SHOWN && (
          <button type="button" onClick={() => setAll(true)} className="min-h-11 text-sm font-medium text-brand-700 dark:text-brand-500">
            Pokaż wszystkie ({history.purchases.length})
          </button>
        )}
      </section>
    </>
  );
}

function PurchaseRow({ p, name }: { p: Purchase; name: string | undefined }) {
  const qty = p.weight ? `${p.qty.toLocaleString('pl-PL', { maximumFractionDigits: 3 })} kg` : `${p.qty} szt.`;
  return (
    <li>
      <Link to={`/receipts/${encodeURIComponent(p.receiptId)}`} className="flex items-center gap-3 px-4 py-2.5 active:bg-stone-50 dark:active:bg-stone-800">
        <div className="min-w-0 flex-1">
          <div className="text-sm">
            {dayLabel(p.ms)}
            {name && <span className="text-stone-500"> · {name}</span>}
          </div>
          <div className="text-xs text-stone-500 tabular-nums">
            {qty} × {formatGr(p.shelfGr)}
            {p.discountGr > 0 && <span className="text-brand-700 dark:text-brand-500"> · {p.discounts ?? 'rabat'} −{formatGr(p.discountGr, false)}</span>}
          </div>
        </div>
        <div className="shrink-0 font-medium tabular-nums">{formatGr(p.totalGr)}</div>
      </Link>
    </li>
  );
}

function ArticleLink({ article }: { article: ArticleRow }) {
  return (
    <Link to={`/articles/${encodeURIComponent(article.id)}`} className="flex items-center gap-3 px-4 py-3 active:bg-stone-50 dark:active:bg-stone-800">
      <div className="min-w-0 flex-1">
        <div className="truncate">{articleName(article)}</div>
        {article.lastBoughtMs && <div className="text-xs text-stone-500">ostatnio {agoLabel(article.lastBoughtMs)}</div>}
      </div>
      {article.lastShelfGr !== null && (
        <div className="shrink-0 font-medium tabular-nums">
          {formatGr(article.lastShelfGr)}
          {article.weight ? '/kg' : ''}
        </div>
      )}
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-stone-500">{label}</dt>
      <dd className="truncate font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-9 max-w-[70vw] shrink-0 truncate rounded-full px-3 text-sm font-medium ${
        active ? 'bg-brand-700 text-white' : 'border border-stone-300 bg-white dark:border-stone-700 dark:bg-stone-900'
      }`}
    >
      {children}
    </button>
  );
}

function ProductPicker({
  open,
  initial,
  hasProduct,
  onClose,
  onPick,
}: {
  open: boolean;
  initial: string;
  hasProduct: boolean;
  onClose: () => void;
  onPick: (productId: Id | null) => void;
}) {
  const [q, setQ] = useState('');
  const query = normKey(q || initial).split(' ')[0] ?? '';
  const products = useLiveQuery(async () => {
    const all = (await db.products.toArray()).filter((p) => !p.deleted && !p.mergedInto);
    const hits = query ? all.filter((p) => p.normKey.includes(query)) : all;
    return hits.sort((a, b) => a.name.localeCompare(b.name, 'pl')).slice(0, 50);
  }, [query]);
  return (
    <BottomSheet open={open} onClose={onClose} title="Który to produkt z list?">
      <div className="flex flex-col gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Szukaj, np. ${initial.split(' ')[0]}`} className={inputCls} />
        <ul className="flex max-h-[45dvh] flex-col divide-y divide-stone-200 overflow-y-auto dark:divide-stone-800">
          {(products ?? []).map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => onPick(p.id)} className="min-h-12 w-full px-1 text-left active:bg-stone-100 dark:active:bg-stone-800">
                {p.name}
              </button>
            </li>
          ))}
          {products?.length === 0 && <li className="py-3 text-sm text-stone-500">Nic nie znalazłem.</li>}
        </ul>
        {hasProduct && (
          <button type="button" onClick={() => onPick(null)} className="min-h-11 text-sm font-medium text-red-600 dark:text-red-400">
            Odepnij od produktu
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

function Loading() {
  return (
    <div className="p-4">
      <SkeletonRows count={4} />
    </div>
  );
}

function Missing() {
  return (
    <>
      <PageHeader title="Historia ceny" back="/prices" />
      <p className="p-8 text-center text-stone-500">Nie ma takiego produktu.</p>
    </>
  );
}
